import AddIcon from '@mui/icons-material/Add';
import { Fab } from '@mui/material';
import * as React from 'react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ExposureConfigurationResponse } from '../../../api/ConfigApi';
import { handleUnauthorizedResponse } from '../../../api/http';
import { AuthData, HIDE_PERIL_LIMITS_ENABLE } from '../../../context/auth';
import { getNewExposure, PolicyData } from '../../../context/policy';
import { checkExposureList, sortByObjectKey } from '../../../util/util';
import { StyledModalContentContainer } from '../../common/index';
import DefaultDialog from '../../DefaultDialog';
import { errorToast, successToast } from '../../Toast';
import ReactSelect, { SelectValue } from '../fields/ReactSelect';

interface Props {
	exposureConfigs: ExposureConfigurationResponse[];
	policyData: PolicyData;
	authData: AuthData;
}

export default function AddExposureModal(props: Props) {
	const { t } = useTranslation();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;
	const { exposureConfigs, policyData, authData } = props;
	const params = useParams();
	const sortedExposures = sortByObjectKey(exposureConfigs, 'displayName');
	const [isOpen, setIsOpen] = useState(false);
	const initialValue = sortedExposures[0]
		? { value: sortedExposures[0].name, label: sortedExposures[0].displayName }
		: { value: '', label: '' };
	const [selectedExposure, setSelectedExposure] = useState(initialValue);
	const close = () => setIsOpen(false);
	const open = () => setIsOpen(true);
	const hidePerilLimitsEnabled =
		authData?.auth?.configProperties?.[HIDE_PERIL_LIMITS_ENABLE] ?? false;

	const { saveFastForm, setLoading } = policyData;
	const navigate = useNavigate();

	const addExposure = async () => {
		await close();
		const exposureConfig = sortedExposures.find(
			(e) => e.name === selectedExposure.value,
		);
		const addExposureReq = getNewExposure(
			exposureConfig,
			hidePerilLimitsEnabled,
		);
		setLoading(true);
		try {
			const updatedPolicyData = await saveFastForm(
				getValues(),
				dirtyFields,
				reset,
				{
					addExposure: addExposureReq,
				},
			);
			const addedExposure = updatedPolicyData?.policy?.exposures?.pop();
			if (addedExposure) {
				successToast('Successfully added exposure');
				navigate(
					`/policy/${params.policyId}/quotes/${params.quoteId}/exposures/${addedExposure.displayId}`,
				);
			}
		} catch (error) {
			errorToast('Failed to add exposure');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};

	const exposureOptions = sortedExposures.map(
		(eC: ExposureConfigurationResponse) => ({
			value: eC.name,
			label: eC.displayName,
		}),
	);

	return (
		<>
			<Fab
				color='primary'
				onClick={() => checkExposureList(sortedExposures, addExposure, open)}
				sx={{ position: 'fixed', bottom: 20, right: 20 }}
				variant='extended'
			>
				<AddIcon sx={{ mr: 1 }} /> {`Add ${t('exposure.singular.upper')}`}
			</Fab>
			<DefaultDialog
				actions={[
					{ text: 'Add', onClick: addExposure },
					{ text: 'Cancel', onClick: close },
				]}
				open={isOpen}
				onClose={close}
				title={`Add ${t('exposure.singular.upper')}`}
			>
				<StyledModalContentContainer>
					<div style={{ width: '50%', height: '40px' }}>
						<ReactSelect
							id='add-exposure-selector'
							options={exposureOptions}
							value={selectedExposure}
							onChange={(opt: SelectValue) => setSelectedExposure(opt)}
							disableOpenMenuOnFocus
						/>
					</div>
				</StyledModalContentContainer>
			</DefaultDialog>
		</>
	);
}
